import {
  flow,
  includes,
  map,
  reject,
  reverse,
  slice,
  sortBy,
  uniqBy,
} from "lodash/fp";

import { getDateSelection } from "@app/analytics/components/DateSelector/utils";
import useMetricsQuery from "@app/analytics/hooks/metrics/useMetricsQuery";
import useTranscript from "@app/analytics/MeetingReport/hooks/useTranscript";
import useEditStore from "@app/analytics/MeetingReport/stores/edits";
import { isAnonymousSpeaker } from "@app/analytics/MeetingReport/utils";
import { ParsedMetric } from "@app/analytics/meetops/metrics";
import { Filter } from "@app/analytics/meetops/types";
import {
  getQueryDateRangesFromSelection,
  getQueryKeyFromDateRanges,
} from "@app/analytics/utils/dateRangeUtils";
import useAuth from "@app/common/auth/useAuth";
import { TranscriptSpeaker } from "@app/common/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";

interface UserData {
  participantTimeInMeeting: ParsedMetric;
  participantName: string;
}

type SuggestionMode = "participants" | "workspace_users" | "edits";

interface UseSuggestedSpeakerNamesProps {
  enabled?: boolean;
  modes: SuggestionMode[];
}

const getTop = flow([
  sortBy((userData: UserData) => userData.participantTimeInMeeting.value ?? 0),
  reverse,
  slice(0, 15),
]);

const findTopUsers = (data: { data: UserData[] }[]): TranscriptSpeaker[] => {
  if (data.length === 0) return [];
  const topUsers: TranscriptSpeaker[] = [];

  const recentData = [...data[0].data];

  const recentTop = getTop(recentData);

  recentTop.map((user: UserData) =>
    topUsers.push({
      name: user.participantName,
    })
  );

  return topUsers;
};

/** Remove anonymous users and duplicates */
const filterSuggestions = (suggestions: { name: string | null }[]) =>
  uniqBy(
    "name",
    reject(({ name }) => isAnonymousSpeaker(name), suggestions)
  );

const useSuggestedWorkspaceUsers = ({ enabled }: { enabled: boolean }) => {
  const { user } = useAuth();

  const { workspace } = useWorkspace();

  const dateRanges = getQueryDateRangesFromSelection(
    getDateSelection({ preset: "14" })
  );

  const suggestedSpeakersQuery = useMetricsQuery(
    {
      dateRanges,
      dimensions: ["participantName", "participantId"],
      filters: [] as Filter[],
      metrics: ["participantHoursInMeeting"],
      userId: user?.id,
      people: "everyone",
    },
    {
      enabled: Boolean(user?.id) && enabled,
      queryKey: [
        "suggestedSpeakerNames",
        user?.id,
        getQueryKeyFromDateRanges(dateRanges),
      ],
      staleTime: Infinity,
      cacheTime: 7200 * 60 * 1000, // 5 days
      refetchOnMount: false,
    }
  );

  const suggestedSpeakers = findTopUsers(
    suggestedSpeakersQuery.data?.results ?? []
  );

  const usersFromWorkspace = map(
    (u) => ({
      name: u.fullName,
    }),
    workspace?.users ?? []
  );

  const uniqueSuggestions = uniqBy("name", [
    ...suggestedSpeakers,
    ...usersFromWorkspace,
  ]);

  return {
    suggestedSpeakers: uniqueSuggestions,
    isLoading: suggestedSpeakersQuery.isLoading,
  };
};

const useParticipants = ({ enabled }: { enabled: boolean }) => {
  const { speakers, isLoading } = useTranscript({ enabled });

  return {
    speakers,
    isLoading,
  };
};

const useSuggestedSpeakerNames = ({
  enabled = true,
  modes,
}: UseSuggestedSpeakerNamesProps) => {
  const includeEditedSpeakers = includes("edits", modes);
  const includeWorkspaceUsers = includes("workspace_users", modes);
  // cannot use both the participants and the edited speakers
  const includeParticipants =
    !includeEditedSpeakers && includes("participants", modes);

  const {
    suggestedSpeakers: workspaceSuggestions,
    isLoading: workspaceSuggestionsLoading,
  } = useSuggestedWorkspaceUsers({
    enabled: enabled && includeWorkspaceUsers,
  });

  const { speakers: participants, isLoading: participantsLoading } =
    useParticipants({
      enabled: enabled && includeParticipants,
    });

  const editedSpeakers = useEditStore((state) => state.transcript.speakers);

  // participants (/edited speakers) take priority over workspace users
  const suggestedSpeakers = [
    ...(includeEditedSpeakers ? editedSpeakers : []),
    ...(includeParticipants ? participants : []),
    ...(includeWorkspaceUsers ? workspaceSuggestions : []),
  ];

  const isLoading =
    (includeWorkspaceUsers && workspaceSuggestionsLoading) ||
    (includeParticipants && participantsLoading);

  return {
    suggestedSpeakers: filterSuggestions(suggestedSpeakers),
    isLoading,
  };
};

export default useSuggestedSpeakerNames;
